import {
  CyberAssessmentEvidencePackChecklistJurisdiction,
  type CyberAssessmentPurpleTeamExerciseSelectDto,
  type CyberAssessmentSelectDto,
  type TechnologySelectDto,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { CyberScoreTile } from '@components/pages/technology/components/cyber-score-tile.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { CountryBadge } from '@components/shared/country-badge.component';
import { CyberQuestionContextComponent } from '@components/shared/cyber-question-context.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { Checkbox } from '@components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { cn } from '@lib/utils';
import {
  CYBER_ASSESSMENT_REPORT_SECTION_OPTIONS,
  type CyberAssessmentActionPlanItemLike,
  type CyberAssessmentBlockingItemLike,
  type CyberAssessmentCmmcGapPriorityGroupCard,
  type CyberAssessmentOverviewMetric,
  type CyberAssessmentReportSection,
  buildCyberAssessmentCompletedReportModel,
  buildCyberAssessmentEvidencePackChecklistModel,
  getCyberAssessmentActionPlanItems,
  getCyberAssessmentBlockingItems,
  getVisibleCyberAssessmentReportSections,
} from '@utils/cyber-assessment/report.util';
import { CheckCircle2, CircleMinusIcon, CircleX, Clock3 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { TechnologyCyberReportHeader } from './technology-cyber-report-header.component';
import {
  formatActionPlanPhaseBadgeLabel,
  formatClassificationBadgeLabel,
  formatCmmcGapPriorityBadgeLabel,
  formatCmmcGapPriorityItemLabel,
  getActionPlanQuestionBadgeClassName,
  getCmmcCoverageToneClassName,
  getCmmcDomainDisplayCounts,
  getCmmcIndicativeLevelPresentation,
  getOverviewMetricBadgeClassName,
  resolveCyberQuestionRagBadgeVariant,
} from './technology-cyber-report.util';

type TechnologyCyberCompletedReportProps = {
  assessment: CyberAssessmentSelectDto;
  canCreate: boolean;
  cyberSection: CyberAssessmentReportSection;
  onCreateAssessment: () => void;
  onReviewAnswers: (assessmentId: string) => void;
  onSectionChange: (value: string) => Promise<void>;
  onToggleChecklistItem: (params: {
    checked: boolean;
    cyberAssessmentId: string;
    itemId: string;
    jurisdiction: CyberAssessmentEvidencePackChecklistJurisdiction;
  }) => Promise<unknown>;
  technology: TechnologySelectDto;
};

type CyberOverviewMetricTileProps = {
  children?: React.ReactNode;
  metric: CyberAssessmentOverviewMetric;
  title: string;
  valueClassName?: string;
  valueContainerClassName?: string;
};

type CyberReportSectionDefinition = {
  content: React.ReactNode;
  id: Exclude<CyberAssessmentReportSection, 'fullReport'>;
  title: React.ReactNode;
};

const REPORT_SUBTITLE_CLASS_NAME = 'text-sm font-semibold text-gray-700';
const RESPONSE_ICON_CLASS_NAME_BY_VALUE: Record<string, string> = {
  IN_PROGRESS: 'text-gray-500',
  NO: 'text-rose-600',
};

const toEvidencePackChecklistJurisdiction = (
  value: string,
): CyberAssessmentEvidencePackChecklistJurisdiction | null => {
  if (value === 'AU') {
    return CyberAssessmentEvidencePackChecklistJurisdiction.AU;
  }

  if (value === 'US') {
    return CyberAssessmentEvidencePackChecklistJurisdiction.US;
  }

  if (value === 'UK') {
    return CyberAssessmentEvidencePackChecklistJurisdiction.UK;
  }

  return null;
};

const EvidencePackChecklistProgressBar: React.FC<{
  progress: number;
}> = (props) => {
  const { progress } = props;

  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full rounded-full bg-[#3D2EFF]"
        style={{ width: `${String(Math.round(progress * 100))}%` }}
      />
    </div>
  );
};

const EvidencePackChecklistCard: React.FC<{
  assessment: CyberAssessmentSelectDto;
  isEditable: boolean;
  onToggleChecklistItem: (params: {
    checked: boolean;
    cyberAssessmentId: string;
    itemId: string;
    jurisdiction: CyberAssessmentEvidencePackChecklistJurisdiction;
  }) => Promise<unknown>;
}> = (props) => {
  const { assessment, isEditable, onToggleChecklistItem } = props;
  const [optimisticChecklist, setOptimisticChecklist] = useState(assessment.evidencePackChecklist ?? null);
  const [pendingItemKey, setPendingItemKey] = useState<string | null>(null);

  useEffect(() => {
    setOptimisticChecklist(assessment.evidencePackChecklist ?? null);
  }, [assessment.evidencePackChecklist]);

  const checklistModel = buildCyberAssessmentEvidencePackChecklistModel({
    evidencePackChecklist: optimisticChecklist ?? assessment.evidencePackChecklist,
  });

  if (checklistModel.isEmpty) {
    return null;
  }

  const buildNextChecklistModel = (params: {
    checked: boolean;
    itemId: string;
    jurisdiction: CyberAssessmentEvidencePackChecklistJurisdiction;
  }) => {
    const { checked, itemId, jurisdiction } = params;
    const currentChecklist = optimisticChecklist ?? assessment.evidencePackChecklist;

    if (!currentChecklist) {
      return null;
    }

    return {
      ...currentChecklist,
      jurisdictions: currentChecklist.jurisdictions.map((jurisdictionSnapshot) => ({
        ...jurisdictionSnapshot,
        items:
          jurisdictionSnapshot.jurisdiction === jurisdiction
            ? jurisdictionSnapshot.items.map((item) => (item.id === itemId ? { ...item, checked } : item))
            : jurisdictionSnapshot.items,
      })),
    };
  };

  const handleCheckedChange = async (params: {
    checked: boolean;
    itemId: string;
    jurisdiction: CyberAssessmentEvidencePackChecklistJurisdiction;
  }): Promise<void> => {
    const { checked, itemId, jurisdiction } = params;
    const previousChecklist = optimisticChecklist ?? assessment.evidencePackChecklist ?? null;
    const nextChecklist = buildNextChecklistModel({
      checked,
      itemId,
      jurisdiction,
    });
    const itemKey = `${jurisdiction}-${itemId}`;

    if (!nextChecklist) {
      return;
    }

    setOptimisticChecklist(nextChecklist);
    setPendingItemKey(itemKey);

    try {
      await onToggleChecklistItem({
        checked,
        cyberAssessmentId: assessment.id,
        itemId,
        jurisdiction,
      });
    } catch (_error) {
      setOptimisticChecklist(previousChecklist);
    } finally {
      setPendingItemKey(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-sm font-semibold text-gray-900">Evidence Pack Checklist</div>
            <div className="text-sm text-gray-500">
              Required evidence artefacts for ATO submission, grouped by jurisdiction.
            </div>
          </div>
          <div className="flex min-w-44 flex-col items-end gap-2">
            <div className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">{checklistModel.checkedItems}</span>/
              {checklistModel.totalItems} collected
            </div>
            <EvidencePackChecklistProgressBar progress={checklistModel.progress} />
          </div>
        </div>

        <Accordion
          className="flex flex-col gap-3"
          defaultValue={checklistModel.jurisdictions[0] ? [checklistModel.jurisdictions[0].jurisdiction] : []}
          type="multiple"
        >
          {checklistModel.jurisdictions.map((jurisdiction) => (
            <AccordionItem
              className="rounded-xl border border-gray-200 px-4"
              key={jurisdiction.jurisdiction}
              value={jurisdiction.jurisdiction}
            >
              <AccordionTrigger className="cursor-pointer gap-4 py-4 hover:no-underline">
                <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <CountryBadge
                      className="shrink-0"
                      jurisdiction={jurisdiction.jurisdictionCode}
                    />
                    <div className="text-sm font-semibold text-gray-900">{jurisdiction.jurisdiction}</div>
                  </div>
                  <div className="flex min-w-44 flex-col gap-2">
                    <div className="text-right text-sm text-gray-500">
                      <span className="font-semibold text-gray-900">{jurisdiction.checkedItems}</span>/
                      {jurisdiction.totalItems}
                    </div>
                    <EvidencePackChecklistProgressBar progress={jurisdiction.progress} />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pt-0">
                <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                  {jurisdiction.items.map((item) => {
                    const itemKey = `${jurisdiction.jurisdiction}-${item.id}`;

                    return (
                      <label
                        className={cn('flex items-start gap-3', isEditable && 'cursor-pointer')}
                        key={itemKey}
                      >
                        {isEditable ? (
                          <Checkbox
                            checked={item.checked}
                            disabled={pendingItemKey === itemKey}
                            onCheckedChange={(checked) => {
                              const nextJurisdiction = toEvidencePackChecklistJurisdiction(
                                jurisdiction.jurisdictionCode,
                              );

                              if (!nextJurisdiction) {
                                return;
                              }

                              void handleCheckedChange({
                                checked: checked === true,
                                itemId: item.id,
                                jurisdiction: nextJurisdiction,
                              });
                            }}
                          />
                        ) : item.checked ? (
                          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                        ) : (
                          <Clock3 className="mt-0.5 size-4 shrink-0 text-amber-500" />
                        )}
                        <span
                          className={cn(
                            'text-sm leading-6 text-gray-700',
                            item.checked && 'text-gray-400',
                            pendingItemKey === itemKey && 'opacity-70',
                          )}
                        >
                          {item.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

const CyberQuestionStatusRow: React.FC<{
  questionId: string;
  ragStatus: string;
  response: string;
}> = (props) => {
  const { questionId, ragStatus, response } = props;
  const ResponseIcon = response === 'NO' ? CircleX : CircleMinusIcon;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge
        size="xs"
        variant={resolveCyberQuestionRagBadgeVariant(ragStatus)}
      >
        {questionId}
      </Badge>
      <span
        className={cn(
          'flex items-center gap-1 text-xs font-medium',
          RESPONSE_ICON_CLASS_NAME_BY_VALUE[response] ?? 'text-gray-500',
        )}
      >
        <ResponseIcon className="size-3.5" />
        {response === 'IN_PROGRESS' ? 'IN PROGRESS' : response}
      </span>
    </div>
  );
};

const CyberStandardsReferenceList: React.FC<{
  references: CyberAssessmentBlockingItemLike['standardsReferences'];
}> = (props) => {
  const { references = [] } = props;

  if (references.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className={REPORT_SUBTITLE_CLASS_NAME}>Standards References</div>
      <div className="flex flex-col gap-2">
        {references.map((reference) => {
          const jurisdiction = reference.jurisdiction ?? 'International';

          return (
            <div
              className="flex flex-wrap items-center gap-2 text-xs text-slate-600"
              key={`${reference.standard}-${reference.section}-${jurisdiction}`}
            >
              <CountryBadge jurisdiction={jurisdiction} />
              {reference.url ? (
                <ExternalLink
                  className="text-xs text-slate-600 hover:text-slate-800"
                  href={reference.url}
                  iconClassName="size-3"
                  iconSize={12}
                  title={`${reference.standard} — ${reference.section}`}
                >
                  <span className="font-medium text-slate-800">{reference.standard}</span>
                  <span>—</span>
                  <span>{reference.section}</span>
                </ExternalLink>
              ) : (
                <>
                  <span className="font-medium text-slate-800">{reference.standard}</span>
                  <span>—</span>
                  <span>{reference.section}</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BlockingDeficiencyAccordionItem: React.FC<{
  item: CyberAssessmentBlockingItemLike;
}> = (props) => {
  const { item } = props;

  return (
    <AccordionItem
      className="rounded-xl border border-rose-200 bg-white px-4"
      value={`${item.questionId}-${item.response}`}
    >
      <AccordionTrigger className="cursor-pointer gap-4 py-4 hover:no-underline">
        <div className="flex min-w-0 flex-1 flex-col gap-2 text-left">
          <CyberQuestionStatusRow
            questionId={item.questionId}
            ragStatus={item.ragStatus ?? 'RED'}
            response={item.response}
          />
          <div className="text-sm font-medium leading-snug text-slate-900">{item.prompt}</div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-5 pt-0">
        <div className="flex flex-col gap-4 border-t border-rose-100 pt-4">
          <CyberQuestionContextComponent missionImpactIfNo={item.missionImpactIfNo} />

          {item.explanation ? (
            <div className="flex flex-col gap-2">
              <BlueLabel
                className="font-semibold"
                text="What You Must Do Next"
              />
              <div className="text-xs leading-5 text-slate-600">{item.explanation}</div>
            </div>
          ) : null}

          <CyberStandardsReferenceList references={item.standardsReferences} />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const ReadinessQuestionRow: React.FC<{
  question: {
    questionId: string;
    questionSummary: string;
    ragStatus: string;
    remediationPriority: string;
    response: string;
  };
}> = (props) => {
  const { question } = props;

  return (
    <div className="rounded-lg border border-gray-200 px-3 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <CyberQuestionStatusRow
          questionId={question.questionId}
          ragStatus={question.ragStatus}
          response={question.response}
        />
        <div className="text-xs leading-5 text-gray-700">{question.questionSummary}</div>
      </div>
    </div>
  );
};

const ActionPlanAccordionItem: React.FC<{
  item: CyberAssessmentActionPlanItemLike;
}> = (props) => {
  const { item } = props;

  return (
    <AccordionItem
      className="rounded-xl border border-gray-200 bg-white px-4"
      value={`${item.questionId}-${item.priority}`}
    >
      <AccordionTrigger className="cursor-pointer gap-4 py-4 hover:no-underline">
        <div className="flex min-w-0 flex-1 flex-col gap-2 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              className={getActionPlanQuestionBadgeClassName(item.priority)}
              size="xs"
            >
              {item.questionId}
            </Badge>
            <Badge
              size="xs"
              variant="outline"
            >
              {formatActionPlanPhaseBadgeLabel(item.phaseTitle)}
            </Badge>
          </div>
          <div className="text-sm font-medium leading-snug text-slate-900">{item.prompt}</div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-5 pt-0">
        <div className="flex flex-col gap-4 border-t border-gray-100 pt-4">
          <CyberQuestionContextComponent
            contentClassName="text-xs leading-5"
            missionImpactIfNo={item.missionImpactIfNo}
            title="Why (Mission Impact)"
          />
          <div className="flex flex-col gap-2">
            <BlueLabel text="What Good Looks Like" />
            <div className="text-xs leading-5 text-slate-600">{item.whatGoodLooksLike ?? 'Not provided.'}</div>
          </div>
          {item.evidenceToAttach && item.evidenceToAttach.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className={REPORT_SUBTITLE_CLASS_NAME}>Evidence To Attach</div>
              <ul className="list-disc space-y-1 pl-5 text-xs leading-5 text-slate-600">
                {item.evidenceToAttach.map((evidence) => (
                  <li key={`${item.questionId}-${evidence}`}>{evidence}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const PurpleTeamExerciseAccordionItem: React.FC<{
  item: CyberAssessmentPurpleTeamExerciseSelectDto;
}> = (props) => {
  const { item } = props;
  const responseLabel =
    item.response === 'YES' ? 'Completed' : item.response === 'IN_PROGRESS' ? 'In Progress' : 'Not Completed';
  const ResponseIcon = item.response === 'YES' ? CheckCircle2 : item.response === 'IN_PROGRESS' ? Clock3 : CircleX;

  return (
    <AccordionItem
      className="rounded-xl border border-gray-200 bg-white px-4"
      value={`${item.id}-${item.response}`}
    >
      <AccordionTrigger className="cursor-pointer gap-4 py-4 hover:no-underline">
        <div className="flex min-w-0 flex-1 flex-col gap-2 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              size="xs"
              variant={resolveCyberQuestionRagBadgeVariant(item.ragStatus)}
            >
              {item.id}
            </Badge>
            <span
              className={cn(
                'flex items-center gap-1 text-xs font-medium',
                item.ragStatus === 'GREEN'
                  ? 'text-emerald-600'
                  : (RESPONSE_ICON_CLASS_NAME_BY_VALUE[item.response] ?? 'text-rose-600'),
              )}
            >
              <ResponseIcon className="size-3.5" />
              {responseLabel}
            </span>
          </div>
          <div className="text-sm font-medium leading-snug text-slate-900">{item.title}</div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-5 pt-0">
        <div className="flex flex-col gap-4 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="flex flex-col gap-2">
              <BlueLabel text="Objective" />
              <div className="text-xs leading-5 text-slate-600">{item.objective}</div>
            </div>
            <div className="flex flex-col gap-2">
              <BlueLabel text="Minimum Frequency" />
              <div className="text-xs leading-5 text-slate-600">{item.minimumFrequency}</div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <BlueLabel text="Current Assessment" />
            <div className="text-xs leading-5 text-slate-600">{item.responseDescription}</div>
            {item.responseOverride ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700">
                {item.responseOverride}
              </div>
            ) : null}
          </div>

          {item.details ? (
            <div className="flex flex-col gap-2">
              <BlueLabel text="Saved Notes" />
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-5 text-slate-600">
                {item.details}
              </div>
            </div>
          ) : null}

          {item.mitreAttackTactics.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className={REPORT_SUBTITLE_CLASS_NAME}>MITRE ATT&CK Tactics</div>
              <div className="flex flex-wrap gap-2">
                {item.mitreAttackTactics.map((tactic) => (
                  <Badge
                    key={`${item.id}-${tactic}`}
                    size="xs"
                    variant="outline"
                  >
                    {tactic}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {item.mitreAttackTechniques.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className={REPORT_SUBTITLE_CLASS_NAME}>MITRE ATT&CK Techniques</div>
              <div className="flex flex-wrap gap-2">
                {item.mitreAttackTechniques.map((technique) => (
                  <Badge
                    key={`${item.id}-${technique.id}`}
                    size="xs"
                    variant="outline"
                  >
                    {technique.id} · {technique.name}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {item.prerequisiteControls.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className={REPORT_SUBTITLE_CLASS_NAME}>Prerequisite Controls</div>
              <div className="flex flex-wrap gap-2">
                {item.prerequisiteControls.map((control) => (
                  <Badge
                    key={`${item.id}-${control}`}
                    size="xs"
                    variant="outline"
                  >
                    {control}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {item.evidenceRequired.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className={REPORT_SUBTITLE_CLASS_NAME}>Evidence Required</div>
              <ul className="list-disc space-y-1 pl-5 text-xs leading-5 text-slate-600">
                {item.evidenceRequired.map((evidence) => (
                  <li key={`${item.id}-${evidence}`}>{evidence}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <CyberStandardsReferenceList references={item.complianceReferences} />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const CmmcGapPriorityAccordionList: React.FC<{
  groups: CyberAssessmentCmmcGapPriorityGroupCard[];
}> = (props) => {
  const { groups } = props;

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-4">
        <div className="text-sm font-semibold text-gray-900">Gap Remediation Priorities</div>
      </div>
      <Accordion
        className="flex flex-col gap-3 p-4"
        defaultValue={[groups[0]?.domainName].filter(Boolean)}
        type="multiple"
      >
        {groups.map((group) => (
          <AccordionItem
            className="rounded-xl border border-gray-200 px-4"
            key={group.domainName}
            value={group.domainName}
          >
            <AccordionTrigger className="cursor-pointer gap-4 py-4 hover:no-underline">
              <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <Badge
                  className="shrink-0 border border-rose-200 bg-rose-100 text-rose-700"
                  size="xs"
                >
                  {formatCmmcGapPriorityBadgeLabel(group.items.length)}
                </Badge>
                <div className="text-sm font-semibold text-gray-900">{group.domainName}</div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4 pt-0">
              <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                {group.items.map((item) => (
                  <div
                    className="rounded-lg border border-gray-200 px-3 py-3"
                    key={`${group.domainName}-${item.practiceId}`}
                  >
                    <div className="flex flex-nowrap items-center gap-2 overflow-hidden text-sm text-gray-900">
                      <Badge
                        className="shrink-0 border border-rose-200 bg-rose-100 text-rose-700"
                        size="xs"
                      >
                        {formatCmmcGapPriorityBadgeLabel(item.practiceId)}
                      </Badge>
                      <span className="truncate">
                        {formatCmmcGapPriorityItemLabel({
                          practiceId: item.practiceId,
                          practiceName: item.practiceName,
                          questionIds: item.questionIds,
                        }).replace(`[${item.practiceId}] `, '')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

const CyberOverviewMetricTile: React.FC<CyberOverviewMetricTileProps> = (props) => {
  const { children, metric, title, valueClassName, valueContainerClassName } = props;

  return (
    <div className="flex min-h-32 flex-col justify-between rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium text-gray-500">{title}</div>
        <div className={cn('flex items-center gap-2', valueContainerClassName)}>
          <div className={cn('text-2xl font-semibold text-gray-900', valueClassName)}>{metric.value}</div>
        </div>
        {metric.description ? <div className="text-xs leading-relaxed text-gray-500">{metric.description}</div> : null}
      </div>
      {children}
    </div>
  );
};

export const TechnologyCyberCompletedReport: React.FC<TechnologyCyberCompletedReportProps> = (props) => {
  const {
    assessment,
    canCreate,
    cyberSection,
    onCreateAssessment,
    onReviewAnswers,
    onSectionChange,
    onToggleChecklistItem,
    technology,
  } = props;
  const reportModel = buildCyberAssessmentCompletedReportModel(assessment);
  const blockingItems = getCyberAssessmentBlockingItems(assessment);
  const actionPlanItems = getCyberAssessmentActionPlanItems(assessment);
  const aukusPillar2 = assessment.reportSnapshot?.aukusPillar2;
  const cmmc = assessment.reportSnapshot?.cmmc;
  const hasCmmcMappedPractices = reportModel.cmmcDomainCards.length > 0;
  const cmmcDisplayCounts = cmmc
    ? getCmmcDomainDisplayCounts(cmmc)
    : {
        notAssessedCount: 0,
        partialCount: 0,
      };
  const aukusInteroperabilityBlockerItems =
    aukusPillar2 &&
    'interoperabilityBlockerItems' in aukusPillar2 &&
    Array.isArray(aukusPillar2.interoperabilityBlockerItems)
      ? aukusPillar2.interoperabilityBlockerItems
      : [];
  const cmmcIndicativeLevelPresentation = getCmmcIndicativeLevelPresentation(cmmc?.indicativeLevel ?? 'Unrated');

  const renderBlockingItemsList = (items: typeof blockingItems) => {
    if (items.length === 0) {
      return (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle2 className="size-5 shrink-0 text-emerald-500" />
          <div className="text-sm text-gray-600">No blocking deficiencies were found.</div>
        </div>
      );
    }

    return (
      <Accordion
        className="flex flex-col gap-3"
        defaultValue={[`${items[0]?.questionId}-${items[0]?.response}`].filter(Boolean)}
        type="multiple"
      >
        {items.map((item) => (
          <BlockingDeficiencyAccordionItem
            item={item}
            key={`${item.questionId}-${item.response}`}
          />
        ))}
      </Accordion>
    );
  };

  const renderActionPlanList = (items: typeof actionPlanItems) => {
    if (items.length === 0) {
      return (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle2 className="size-5 shrink-0 text-emerald-500" />
          <div className="text-sm text-gray-600">No action-plan items in this report snapshot.</div>
        </div>
      );
    }

    return (
      <Accordion
        className="flex flex-col gap-3"
        defaultValue={[`${items[0]?.questionId}-${items[0]?.priority}`].filter(Boolean)}
        type="multiple"
      >
        {items.map((item) => (
          <ActionPlanAccordionItem
            item={item}
            key={`${item.questionId}-${item.priority}`}
          />
        ))}
      </Accordion>
    );
  };

  const renderPurpleTeamExerciseList = (items: typeof reportModel.purpleTeamIssueItems) => {
    if (reportModel.purpleTeamExerciseItems.length === 0) {
      return (
        <div className="flex items-center gap-2 py-2">
          <Clock3 className="size-5 shrink-0 text-gray-400" />
          <div className="text-sm text-gray-600">No Purple Team exercise results were captured in this report.</div>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="size-5 shrink-0 text-emerald-600" />
            <div className="text-sm font-medium text-emerald-800">All Purple Team exercises are green.</div>
          </div>
          <div className="pt-2 text-sm text-emerald-700">
            {reportModel.purpleTeamGreenCount}/{reportModel.purpleTeamExerciseItems.length} exercises are completed with
            no current yellow or red findings.
          </div>
        </div>
      );
    }

    return (
      <Accordion
        className="flex flex-col gap-3"
        defaultValue={[`${items[0]?.id}-${items[0]?.response}`].filter(Boolean)}
        type="multiple"
      >
        {items.map((item) => (
          <PurpleTeamExerciseAccordionItem
            item={item}
            key={`${item.id}-${item.response}`}
          />
        ))}
      </Accordion>
    );
  };

  const questionPromptMap = new Map<string, string>();

  for (const item of blockingItems) {
    questionPromptMap.set(item.questionId, item.prompt);
  }

  for (const card of reportModel.readinessCards) {
    for (const question of card.questions) {
      if (!questionPromptMap.has(question.questionId)) {
        questionPromptMap.set(question.questionId, question.questionSummary);
      }
    }
  }

  const sectionDefinitions: CyberReportSectionDefinition[] = [
    {
      content: (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
            <CyberScoreTile
              assessment={assessment}
              canManageAssessments={canCreate}
              cpiPercentage={reportModel.overviewSummary.cpiPercentage}
              onReviewAnswers={() => onReviewAnswers(assessment.id)}
              onStartNewAssessment={onCreateAssessment}
              technology={technology}
            />

            <div className="@container/cyber-overview-metrics">
              <div className="grid grid-cols-1 gap-4 @min-[28rem]/cyber-overview-metrics:grid-cols-2">
                <CyberOverviewMetricTile
                  metric={reportModel.overviewSummary.deploymentReadiness}
                  title="Deployment Readiness"
                  valueClassName={cn(
                    'inline-flex w-fit rounded-full px-3 py-1 text-lg',
                    getOverviewMetricBadgeClassName(reportModel.overviewSummary.deploymentReadiness.value),
                  )}
                />
                <CyberOverviewMetricTile
                  metric={reportModel.overviewSummary.cmmcIndicativeLevel}
                  title="CMMC Indicative Level"
                  valueClassName={cn(
                    'inline-flex w-fit rounded-full px-3 py-1 text-lg',
                    cmmcIndicativeLevelPresentation.badgeClassName,
                  )}
                />
                <CyberOverviewMetricTile
                  metric={reportModel.overviewSummary.aukusReadiness}
                  title="AUKUS Readiness"
                  valueClassName={cn(
                    'inline-flex w-fit rounded-full px-3 py-1 text-lg',
                    getOverviewMetricBadgeClassName(reportModel.overviewSummary.aukusReadiness.value),
                  )}
                />
                <CyberOverviewMetricTile
                  metric={reportModel.overviewSummary.aukusCommonCeiling}
                  title="AUKUS Common Ceiling"
                  valueClassName={cn(
                    'inline-flex w-fit rounded-full px-3 py-1 text-lg',
                    getOverviewMetricBadgeClassName(reportModel.overviewSummary.aukusCommonCeiling.value),
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      ),
      id: 'overview',
      title: 'Overview',
    },
    {
      content: renderBlockingItemsList(blockingItems),
      id: 'blockingDeficiencies',
      title: blockingItems.length > 0 ? `Blocking Deficiencies (${blockingItems.length})` : 'Blocking Deficiencies',
    },
    {
      content: (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            {reportModel.readinessCards.map((card) => (
              <div
                className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5"
                key={card.jurisdiction}
              >
                <div className="flex items-center gap-2">
                  <CountryBadge jurisdiction={card.jurisdictionCode} />
                  <div className="text-lg font-semibold text-gray-900">{card.jurisdiction}</div>
                </div>
                <div className="text-sm text-gray-600">{card.atoPathway}</div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <BlueLabel text="Classification Ceiling" />
                    <div className="w-fit">
                      <Badge
                        className=""
                        size="lg"
                        variant="outline"
                      >
                        {formatClassificationBadgeLabel(card.classificationCeiling)}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500">{card.classificationCeilingRationale}</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <BlueLabel text="Deployment Readiness" />
                    <div className="w-fit">
                      <Badge
                        className=""
                        size="lg"
                        variant="outline"
                      >
                        {card.deploymentReadinessBand}
                      </Badge>
                    </div>
                  </div>
                </div>

                {card.questions.length > 0 ? (
                  <div className="flex flex-col gap-3 pt-2">
                    <div className={REPORT_SUBTITLE_CLASS_NAME}>Blocking Questions ({card.questions.length})</div>
                    <div className="flex flex-col gap-2">
                      {card.questions.map((question) => (
                        <ReadinessQuestionRow
                          key={`${card.jurisdiction}-${question.questionId}`}
                          question={question}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {card.keyControls.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className={REPORT_SUBTITLE_CLASS_NAME}>Key Controls</div>
                    <div className="flex flex-wrap gap-2">
                      {card.keyControls.map((control) => (
                        <Badge
                          key={`${card.jurisdiction}-${control}`}
                          size="xs"
                          variant="outline"
                        >
                          {control}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}

                {card.notes.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className={REPORT_SUBTITLE_CLASS_NAME}>Jurisdiction Notes</div>
                    <div className="grid grid-cols-1 gap-2">
                      {card.notes.map((note, index) => (
                        <div
                          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                          key={`${card.jurisdiction}-note-${String(index)}`}
                        >
                          <div className="text-[11px] font-semibold text-gray-500">{note.label}</div>
                          <div className="mt-1 text-xs leading-5 text-gray-600">{note.body}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <EvidencePackChecklistCard
            assessment={assessment}
            isEditable={canCreate}
            onToggleChecklistItem={onToggleChecklistItem}
          />
        </div>
      ),
      id: 'readinessComparison',
      title: 'Readiness Comparison',
    },
    {
      content: (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[0.9fr_0.9fr_1.2fr]">
              <div className="flex flex-col gap-2">
                <div className={REPORT_SUBTITLE_CLASS_NAME}>Lowest Common Ceiling</div>
                <div
                  className={cn(
                    'w-fit rounded-full px-3 py-1 text-base font-semibold',
                    getOverviewMetricBadgeClassName(aukusPillar2?.lowestCommonClassificationCeiling ?? 'N/A'),
                  )}
                >
                  {aukusPillar2?.lowestCommonClassificationCeiling ?? 'N/A'}
                </div>
                <div className="text-xs leading-relaxed text-gray-500">
                  Highest shared classification posture currently supported across AU, US, and UK.
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className={REPORT_SUBTITLE_CLASS_NAME}>AUKUS Readiness</div>
                <div
                  className={cn(
                    'w-fit rounded-full px-3 py-1 text-base font-semibold',
                    getOverviewMetricBadgeClassName(aukusPillar2?.readinessBand ?? 'N/A'),
                  )}
                >
                  {aukusPillar2?.readinessBand ?? 'N/A'}
                </div>
                <div className="text-xs leading-relaxed text-gray-500">
                  Derived from classification-gate eligibility and interoperability blocker count.
                </div>
              </div>
              <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 xl:border-l xl:border-t-0 xl:pl-5 xl:pt-0">
                <div className={REPORT_SUBTITLE_CLASS_NAME}>Pillar II Gates</div>
                {reportModel.aukusGateCards.map((gate) => (
                  <div
                    className="flex items-center justify-between"
                    key={gate.classificationGate}
                  >
                    <span className="text-sm text-gray-900">{gate.classificationGate}</span>
                    <Badge
                      size="xs"
                      variant={gate.isEligible ? 'success' : 'destructive'}
                    >
                      {gate.isEligible ? 'MET' : 'NOT MET'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {aukusInteroperabilityBlockerItems.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className={REPORT_SUBTITLE_CLASS_NAME}>
                Interoperability Blockers ({aukusInteroperabilityBlockerItems.length})
              </div>
              <div className="rounded-xl border border-gray-200 bg-white">
                {aukusInteroperabilityBlockerItems.map((blocker, index) => {
                  const prompt = blocker.questionSummary || questionPromptMap.get(blocker.questionId);

                  return (
                    <div
                      className={cn(
                        'flex items-start justify-between gap-4 px-4 py-3',
                        index > 0 && 'border-t border-gray-100',
                      )}
                      key={`blocker-${blocker.questionId}-${String(index)}`}
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            size="xs"
                            variant="destructive"
                          >
                            {blocker.questionId}
                          </Badge>
                          {blocker.gateTags.map((gateTag) => (
                            <Badge
                              className="shrink-0 whitespace-nowrap"
                              key={`${blocker.questionId}-${gateTag}`}
                              size="xs"
                              variant="destructive"
                            >
                              {gateTag}
                            </Badge>
                          ))}
                        </div>
                        {prompt ? <div className="text-sm leading-relaxed text-gray-600">{prompt}</div> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ),
      id: 'aukusPillar2',
      title: 'AUKUS & Pillar II',
    },
    {
      content: (
        <div className="flex flex-col gap-4">
          <div className="@container/cmmc-summary rounded-xl border border-gray-200 bg-white p-5">
            <div className="grid grid-cols-1 gap-4 @min-[52rem]/cmmc-summary:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]">
              <div className="flex flex-col gap-2">
                <div className={REPORT_SUBTITLE_CLASS_NAME}>Indicative CMMC Level</div>
                <div
                  className={cn(
                    'w-fit rounded-full px-4 py-1.5 text-base font-semibold',
                    cmmcIndicativeLevelPresentation.badgeClassName,
                  )}
                >
                  {cmmc?.indicativeLevel ?? 'Unrated'}
                </div>
                <div className="text-xs leading-relaxed text-gray-500">
                  {cmmcIndicativeLevelPresentation.description}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 @min-[28rem]/cmmc-summary:grid-cols-2 @min-[40rem]/cmmc-summary:grid-cols-3">
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500">Met</div>
                  <div className="text-2xl font-semibold text-emerald-600">{cmmc?.practicesMet ?? 0}</div>
                </div>
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500">Partial</div>
                  <div className="text-2xl font-semibold text-amber-600">{cmmcDisplayCounts.partialCount}</div>
                </div>
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500">Not Met</div>
                  <div className="text-2xl font-semibold text-rose-600">{cmmc?.practicesNotMet ?? 0}</div>
                </div>
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    Not Assessed
                    <TooltipComponent side="bottom">
                      Practices that could not be directly assessed for this profile, including SCRDF mapping gaps.
                    </TooltipComponent>
                  </div>
                  <div className="text-2xl font-semibold text-gray-400">{cmmcDisplayCounts.notAssessedCount}</div>
                </div>
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500">L2 Coverage</div>
                  <div
                    className={cn(
                      'text-2xl font-semibold',
                      getCmmcCoverageToneClassName(cmmc?.level2CoveragePercentage ?? 0),
                    )}
                  >
                    {cmmc?.level2CoveragePercentage ?? 0}%
                  </div>
                </div>
                <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500">L3 Coverage</div>
                  <div
                    className={cn(
                      'text-2xl font-semibold',
                      getCmmcCoverageToneClassName(cmmc?.level3CoveragePercentage ?? 0),
                    )}
                  >
                    {cmmc?.level3CoveragePercentage ?? 0}%
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-4">
              <div className="text-sm font-semibold text-gray-900">Domain Breakdown</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/80">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <div className="flex items-center gap-1">
                        Domain
                        <TooltipComponent side="bottom">
                          CMMC 2.0 control family (e.g. Access Control, Incident Response).
                        </TooltipComponent>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      Total
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      <div className="flex items-center justify-end gap-1">
                        Met
                        <TooltipComponent side="bottom">
                          All mapped questions answered YES or NOT APPLICABLE.
                        </TooltipComponent>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      <div className="flex items-center justify-end gap-1">
                        Partial
                        <TooltipComponent side="bottom">
                          At least one mapped question answered IN PROGRESS, none answered NO.
                        </TooltipComponent>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      <div className="flex items-center justify-end gap-1">
                        Not Met
                        <TooltipComponent side="bottom">
                          At least one mapped question answered NO or not yet answered.
                        </TooltipComponent>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                      <div className="flex items-center justify-end gap-1">
                        Not Assessed
                        <TooltipComponent side="bottom">
                          Practices that could not be directly assessed for this profile, including SCRDF mapping gaps.
                        </TooltipComponent>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hasCmmcMappedPractices ? (
                    reportModel.cmmcDomainCards.map((domain) => {
                      const { notAssessedCount, partialCount } = getCmmcDomainDisplayCounts(domain);

                      return (
                        <tr
                          className="border-b border-gray-100 last:border-b-0"
                          key={domain.domainName}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{domain.domainName}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">{domain.practicesTotal}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-emerald-600">
                            {domain.practicesMet}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-amber-600">{partialCount}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-rose-600">
                            {domain.practicesNotMet}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-400">{notAssessedCount}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        className="px-4 py-6 text-sm text-gray-500"
                        colSpan={6}
                      >
                        No CMMC practice mappings apply to this technology and TRL profile in the current SCRDF dataset.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <CmmcGapPriorityAccordionList groups={reportModel.cmmcGapPriorityGroups} />
        </div>
      ),
      id: 'cmmc',
      title: 'CMMC',
    },
    {
      content: renderActionPlanList(actionPlanItems),
      id: 'actionPlan',
      title: actionPlanItems.length > 0 ? `Action Plan (${actionPlanItems.length})` : 'Action Plan',
    },
    {
      content: renderPurpleTeamExerciseList(reportModel.purpleTeamIssueItems),
      id: 'purpleTeamExercises',
      title:
        reportModel.purpleTeamIssueItems.length > 0
          ? `Purple Team Exercises (${reportModel.purpleTeamIssueItems.length})`
          : 'Purple Team Exercises',
    },
  ];
  const selectedSectionLabel =
    CYBER_ASSESSMENT_REPORT_SECTION_OPTIONS.find((option) => option.value === cyberSection)?.label ?? 'Full report';
  const visibleSectionIds = getVisibleCyberAssessmentReportSections(cyberSection);
  const visibleSectionDefinitions = sectionDefinitions.filter((definition) =>
    visibleSectionIds.includes(definition.id),
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
        <TechnologyCyberReportHeader
          completedAt={assessment.updatedAt}
          technologyTypes={assessment.technologyTypes}
          trlBand={assessment.trlBand}
        />

        <Select
          onValueChange={(value) => {
            void onSectionChange(value);
          }}
          value={cyberSection}
        >
          <SelectTrigger className="min-w-[220px] bg-white sm:w-[220px]">
            <SelectValue placeholder="Filter sections">{selectedSectionLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {CYBER_ASSESSMENT_REPORT_SECTION_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-8 p-4">
        {visibleSectionDefinitions.map((definition, index) => (
          <div
            className={cn('flex flex-col gap-4 scroll-mt-24', index > 0 && 'border-t border-[#E1E1E1] pt-8')}
            id={`section-${definition.id}`}
            key={definition.id}
          >
            {definition.id !== 'overview' ? (
              <div className="text-xl font-medium text-gray-700">{definition.title}</div>
            ) : null}
            {definition.content}
          </div>
        ))}
      </div>
    </div>
  );
};
